// A new business's first steps, walked through in Ask Ogma (Craig, 31 Oct 2026: Ask Ogma "should also be the place the
// user starts on their first set-up"). After the business name, everything happens in the chat, one step at a time: what
// kind of business it is, the contacts it already has, its first firms, its first campaign, a mailbox, its first emails.
// Each step either asks Ogma (which answers and acts in the chat) or opens its page beside the chat; nothing is skipped
// that a person must approve. The steps after the contacts are Get started's (lib/findStart.ts), read the same way.
import type { Crm } from "./crm";
import { findStartSteps, loadFindStart, type FindStartStep } from "./findStart";
import { sectorOf, SECTOR_KEYS, SECTOR_LABELS } from "./sectors";
import type { JsonObject } from "./types";

export type FirstStepKey = "sector" | "contacts" | FindStartStep["key"];

/** What a step's buttons do: ask Ogma, open a page beside the chat, choose the sector, or skip the step. */
export type FirstStepMove =
  | { kind: "ask"; label: string; text: string }
  | { kind: "page"; label: string; path: string; title: string }
  | { kind: "skip"; label: string };

export interface FirstStep {
  key: FirstStepKey;
  label: string;
  hint: string;
  done: boolean;
  /** Its turn has come (approving waits for the first drafts). */
  due: boolean;
  moves: FirstStepMove[];
}

/** The sectors, as tap answers. */
export const SECTOR_ANSWERS = SECTOR_KEYS.map((k) => ({ key: k, label: SECTOR_LABELS[k] }));

const MOVES: Record<FindStartStep["key"], FirstStepMove[]> = {
  firms: [{ kind: "ask", label: "Find firms in my niche", text: "Run a new search for companies in my niche" }],
  campaign: [{ kind: "ask", label: "Build my first campaign", text: "Build me a campaign for those firms" },
    { kind: "page", label: "Open Campaigns", path: "/campaigns/list", title: "Campaigns" }],
  mailbox: [{ kind: "page", label: "Connect my email", path: "/settings/sending", title: "Connect your email" }],
  approve: [{ kind: "page", label: "Read my first emails", path: "/campaigns/to-approve", title: "Your first emails" }],
};

/** The first steps from what the business has done so far. */
export function firstSteps(f: { config: JsonObject | null; knownContacts: number; contactsSkipped: boolean; find: FindStartStep[] }): FirstStep[] {
  return [
    {
      key: "sector", label: "Tell Ogma what kind of business you are", hint: "It sets Ogma's starting settings for your sector. You can change it later.",
      done: sectorOf(f.config) !== null, due: true, moves: [],
    },
    {
      key: "contacts", label: "Bring in the contacts you already have",
      hint: "Ogma never cold-emails anyone you already know, and your contacts land in your CRM. A file, HubSpot or Pipedrive.",
      done: f.knownContacts > 0 || f.contactsSkipped, due: true,
      moves: [{ kind: "page", label: "Import my contacts", path: "/settings/data", title: "Import your contacts" }, { kind: "skip", label: "Skip for now" }],
    },
    ...f.find.map((s) => ({ key: s.key, label: s.label, hint: s.hint, done: s.done, due: s.due, moves: MOVES[s.key] })),
  ];
}

export const firstStepsDone = (steps: FirstStep[]) => steps.every((s) => s.done);

/** The step to do now: the first not done whose turn has come. */
export const nextStep = (steps: FirstStep[]) => steps.find((s) => !s.done && s.due) ?? null;

// Skipping the contacts is remembered in this browser only: it is a convenience, and the step comes back if storage is
// cleared, which does no harm.
const SKIP_KEY = (tenant: string) => `ogma-first-steps-skip-contacts:${tenant}`;
export function contactsSkipped(tenant: string): boolean {
  try {
    return localStorage.getItem(SKIP_KEY(tenant)) === "1";
  } catch {
    return false;
  }
}
export function skipContacts(tenant: string): void {
  try {
    localStorage.setItem(SKIP_KEY(tenant), "1");
  } catch {
    /* storage blocked: the step simply shows again */
  }
}

export async function loadFirstSteps(crm: Crm, config: JsonObject | null): Promise<FirstStep[]> {
  const [find, known] = await Promise.all([
    loadFindStart(crm).catch(() => findStartSteps({ mailbox: false, campaigns: [] })),
    crm.knownContactsSummary().then((k) => k?.total ?? 0, () => 0),
  ]);
  return firstSteps({ config, knownContacts: known, contactsSkipped: contactsSkipped(crm.tenantId), find });
}
